<template>
  <div class="resident-manager-modern">
    <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
      <!-- Header Section -->
      <div class="manager-header">
        <div class="header-content">
          <h3 class="manager-title">
            <i class="fas fa-user-plus title-icon"></i>
            Gerenciar Residentes
          </h3>
          <p class="manager-subtitle">
            Selecione residentes para adicionar como clientes no sistema
            financeiro
          </p>
        </div>
      </div>

      <!-- Empty State -->
      <div v-show="residents.length == 0" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-users"></i>
        </div>
        <h4 class="empty-title">Nenhum residente encontrado</h4>
        <p class="empty-description">
          Não há residentes disponíveis para adicionar como clientes financeiros
        </p>
      </div>

      <!-- Content Section -->
      <div v-show="residents.length > 0" class="content-section">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filter-header">
            <h4 class="filter-title">
              <i class="fas fa-filter mr-2"></i>
              Filtros de Busca
            </h4>
          </div>

          <div class="filters-grid">
            <!-- Status Filter -->
            <div class="filter-group">
              <label class="filter-label">Status dos Residentes</label>
              <div class="checkbox-group">
                <div class="custom-checkbox">
                  <input
                    class="checkbox-input"
                    type="checkbox"
                    v-model="active"
                    @change="handleResidents"
                    id="active"
                  />
                  <label class="checkbox-label" for="active">
                    <span class="checkbox-indicator"></span>
                    <span class="checkbox-text">Ativos</span>
                  </label>
                </div>

                <div class="custom-checkbox">
                  <input
                    class="checkbox-input"
                    type="checkbox"
                    v-model="inactive"
                    @change="handleResidents"
                    id="inactive"
                  />
                  <label class="checkbox-label" for="inactive">
                    <span class="checkbox-indicator"></span>
                    <span class="checkbox-text">Inativos</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Search Filter -->
            <div class="filter-group">
              <label class="filter-label">Pesquisar Residente</label>
              <div class="search-container">
                <div class="search-input-wrapper">
                  <input
                    type="text"
                    class="search-input"
                    placeholder="Digite o nome do residente..."
                    @input="filter($event.target.value)"
                  />
                  <div class="search-icon">
                    <i class="fas fa-search"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Messages -->
        <ErrorsMessages :errors="errors" />

        <!-- Results Header -->
        <div class="results-header">
          <div class="results-info">
            <span class="results-count">
              {{ filtered.length }} residente(s) encontrado(s)
            </span>
          </div>
          <div class="table-headers">
            <div class="header-item name-header">
              <i class="fas fa-user mr-2"></i>
              Nome do Residente
            </div>
            <div class="header-item status-header">
              <i class="fas fa-circle mr-2"></i>
              Status
            </div>
            <div class="header-item action-header">
              <i class="fas fa-cogs mr-2"></i>
              Ações
            </div>
          </div>
        </div>

        <!-- Residents List -->
        <div class="residents-container">
          <div class="residents-list">
            <div
              v-for="(resident, key) in filtered"
              :key="key"
              class="resident-card"
            >
              <!-- Resident Avatar -->
              <div class="resident-avatar">
                <div class="avatar-circle">
                  <i class="fas fa-user"></i>
                </div>
                <div
                  class="status-indicator"
                  :class="{
                    active: resident.status_type_value == 'ATIVO',
                    inactive: resident.status_type_value == 'INATIVO',
                  }"
                ></div>
              </div>

              <!-- Resident Info -->
              <div class="resident-info">
                <h5 class="resident-name">{{ resident.name }}</h5>
                <div class="resident-details">
                  <span class="resident-id">ID: {{ resident.id }}</span>
                </div>
              </div>

              <!-- Status Badge -->
              <div class="status-section">
                <span
                  class="status-badge"
                  :class="{
                    'status-active': resident.status_type_value == 'ATIVO',
                    'status-inactive': resident.status_type_value == 'INATIVO',
                  }"
                >
                  <i
                    :class="[
                      'fas',
                      resident.status_type_value == 'ATIVO'
                        ? 'fa-check-circle'
                        : 'fa-times-circle',
                    ]"
                    class="status-icon"
                  ></i>
                  {{ resident.status_type_name }}
                </span>
              </div>

              <!-- Action Button -->
              <div class="action-section">
                <confirmation-dialog
                  small
                  headerClass="modal-header-i"
                  :message-title="'Confirmar Adição'"
                  :message-body="`Deseja realmente adicionar ${resident.name} como cliente no sistema financeiro?`"
                  open-class="add-resident-btn"
                  open-left-icon="fa-plus"
                  open-text="Adicionar"
                  @yes="create(resident.id)"
                />
              </div>
            </div>
          </div>

          <!-- No Results State -->
          <div
            v-if="filtered.length === 0 && residents.length > 0"
            class="no-results"
          >
            <div class="no-results-icon">
              <i class="fas fa-search"></i>
            </div>
            <h4 class="no-results-title">Nenhum resultado encontrado</h4>
            <p class="no-results-description">
              Tente ajustar os filtros ou termo de busca para encontrar
              residentes
            </p>
          </div>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import ErrorsMessages from "../../Components/ErrorsMessages";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import api from "../../../services/api";
import toast from "../../../lib/ToastNotification";

export default {
  name: "Resident",
  components: {
    ErrorsMessages,
    ConfirmationDialog,
  },
  data() {
    return {
      data: [],
      active: true,
      inactive: false,
      filtered: [],
      residents: [],
      loading: false,
      errors: [],
    };
  },
  created() {
    this.retrieve();
  },
  methods: {
    handleResidents() {
      let { active, inactive, residents } = this;

      if (active && !inactive) {
        let actives = residents.filter(
          ({ status_type_value }) => String(status_type_value) == "ATIVO"
        );
        this.filtered = actives;
        this.data = actives;
      }

      if (inactive && !active) {
        let inactives = residents.filter(
          ({ status_type_value }) => String(status_type_value) == "INATIVO"
        );
        this.filtered = inactives;
        this.data = inactives;
      }

      if (inactive && active) {
        this.filtered = residents;
        this.data = residents;
      }

      if (!inactive && !active) {
        this.filtered = [];
        this.data = [];
      }
    },

    filter(text) {
      let { data } = this;

      let filtered = data.filter(
        ({ name }) =>
          String(name).toLowerCase().indexOf(String(text).toLowerCase()) > -1
      );

      this.filtered = filtered;
    },

    retrieve() {
      this.getResidents();
    },

    async getResidents() {
      try {
        this.loading = true;

        const response = await api.get("/rest-home/financial/residents");

        const { data } = response.data;

        this.data = data;
        this.residents = data;
        this.filtered = data;

        this.handleResidents();

        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },

    async create(resident_id) {
      try {
        this.loading = true;

        let params = { resident_id };

        const response = await api.post(
          "/rest-home/financial/residents",
          params
        );

        const { error, errors, message } = response.data;

        if (!error) {
          toast(message, "success");
          this.$bvModal.hide("resident");
          this.$emit("success");
          this.errors = [];
        } else {
          this.errors = errors;
        }
        this.loading = false;
      } catch (error) {
        console.log(error);
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.resident-manager-modern {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
  padding: 24px;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 400px;
}

/* Header Section */
.manager-header {
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 0;
}

.header-content {
  text-align: center;
}

.manager-title {
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
  font-size: 24px;
}

.manager-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 16px;
}

/* Empty State */
.empty-state {
  background: white;
  border-radius: 0 0 16px 16px;
  padding: 80px 32px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 32px;
  color: #9ca3af;
}

.empty-title {
  font-size: 24px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.empty-description {
  color: #64748b;
  font-size: 16px;
  margin: 0;
  max-width: 400px;
  margin: 0 auto;
}

/* Content Section */
.content-section {
  background: white;
  border-radius: 0 0 16px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

/* Filters Section */
.filters-section {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 24px 32px;
}

.filter-header {
  margin-bottom: 20px;
}

.filter-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0;
}

.checkbox-group {
  display: flex;
  gap: 20px;
}

.custom-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.checkbox-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin: 0;
}

.checkbox-indicator {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 4px;
  position: relative;
  transition: all 0.3s ease;
  background: white;
}

.checkbox-input:checked + .checkbox-label .checkbox-indicator {
  background: #0f65b0;
  border-color: #0f65b0;
}

.checkbox-input:checked + .checkbox-label .checkbox-indicator::after {
  content: "✓";
  position: absolute;
  color: white;
  font-size: 12px;
  font-weight: bold;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-text {
  font-size: 14px;
  color: #374151;
  font-weight: 500;
}

/* Search Container */
.search-container {
  position: relative;
}

.search-input-wrapper {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.search-input:focus {
  outline: none;
  border-color: #0f65b0;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1);
}

.search-input::placeholder {
  color: #9ca3af;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

/* Results Header */
.results-header {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px 32px;
}

.results-info {
  margin-bottom: 16px;
}

.results-count {
  font-size: 14px;
  font-weight: 600;
  color: #0f65b0;
  background: rgba(15, 101, 176, 0.1);
  padding: 6px 12px;
  border-radius: 20px;
}

.table-headers {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 20px;
  align-items: center;
}

.header-item {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.name-header {
  justify-self: start;
}

.status-header {
  justify-self: center;
}

.action-header {
  justify-self: end;
}

/* Residents Container */
.residents-container {
  padding: 24px 32px;
  max-height: 600px;
  overflow-y: auto;
}

.residents-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Resident Card */
.resident-card {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  gap: 20px;
  align-items: center;
  padding: 20px;
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.resident-card:hover {
  background: #f1f5f9;
  border-color: #0f65b0;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(15, 101, 176, 0.15);
}

/* Resident Avatar */
.resident-avatar {
  position: relative;
}

.avatar-circle {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
}

.status-indicator {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid white;
}

.status-indicator.active {
  background: #10b981;
}

.status-indicator.inactive {
  background: #ef4444;
}

/* Resident Info */
.resident-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.resident-name {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.resident-details {
  display: flex;
  gap: 12px;
}

.resident-id {
  font-size: 12px;
  color: #6b7280;
  background: #e5e7eb;
  padding: 2px 8px;
  border-radius: 12px;
}

/* Status Section */
.status-section {
  display: flex;
  justify-content: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-badge.status-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.status-icon {
  font-size: 12px;
}

/* Action Section */
.action-section {
  display: flex;
  justify-content: flex-end;
}

.add-resident-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 20px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.add-resident-btn:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 60px 24px;
}

.no-results-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
  color: #9ca3af;
}

.no-results-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px 0;
}

.no-results-description {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Scrollbar personalizada */
.residents-container::-webkit-scrollbar {
  width: 8px;
}

.residents-container::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.residents-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.residents-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Responsive Design */
@media (max-width: 768px) {
  .resident-manager-modern {
    padding: 16px;
  }

  .manager-header {
    padding: 24px 20px;
  }

  .manager-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }

  .filters-section {
    padding: 20px 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .checkbox-group {
    flex-direction: column;
    gap: 12px;
  }

  .results-header {
    padding: 16px;
  }

  .table-headers {
    display: none;
  }

  .residents-container {
    padding: 16px;
  }

  .resident-card {
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    gap: 16px;
    padding: 16px;
  }

  .resident-avatar {
    grid-row: 1 / 3;
  }

  .resident-info {
    grid-column: 2;
    grid-row: 1;
  }

  .status-section {
    grid-column: 1 / 3;
    grid-row: 2;
    justify-content: flex-start;
  }

  .action-section {
    grid-column: 1 / 3;
    grid-row: 3;
    justify-content: stretch;
    margin-top: 12px;
  }

  .add-resident-btn {
    width: 100% !important;
    justify-content: center !important;
  }

  .empty-state {
    padding: 60px 20px;
  }

  .empty-icon {
    width: 64px;
    height: 64px;
    font-size: 24px;
  }

  .empty-title {
    font-size: 20px;
  }

  .empty-description {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .resident-card {
    padding: 12px;
    gap: 12px;
  }

  .avatar-circle {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .status-indicator {
    width: 14px;
    height: 14px;
  }

  .resident-name {
    font-size: 14px;
  }

  .resident-id {
    font-size: 11px;
  }

  .status-badge {
    font-size: 11px;
    padding: 4px 8px;
  }
}
</style>